import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Pricing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
  }, []);

  const nameOf = (p) => (lang === "fr" ? p.name_fr : p.name_en);
  const tagOf = (p) => (lang === "fr" ? p.tagline_fr : p.tagline_en);
  const featsOf = (p) => (lang === "fr" ? p.features_fr : p.features_en);
  const fmtPrice = (v) => (v % 1 === 0 ? String(v) : v.toFixed(2).replace(".", lang === "fr" ? "," : "."));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-head font-black text-4xl sm:text-5xl text-center mb-3">{t("pricing_title")}</h1>
      <p className="text-center text-stone-600 text-lg mb-12 max-w-2xl mx-auto">{t("pricing_sub")}</p>

      <div className="grid md:grid-cols-3 gap-6 mb-20">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`}
            className={`rounded-3xl border-2 p-8 flex flex-col ${p.highlight ? "border-primary bg-white shadow-xl relative lg:scale-105" : "border-stone-200 bg-white"}`}>
            {p.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bt-gradient text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
                <Star size={13} /> {t("pricing_popular")}
              </span>
            )}
            <h3 className="font-head font-black text-2xl">{nameOf(p)}</h3>
            <p className="text-stone-500 mb-4">{tagOf(p)}</p>
            <div className="mb-6">
              <span className="font-head font-black text-4xl">{p.price === 0 ? (lang === "fr" ? "0€" : "$0") : (lang === "fr" ? `${fmtPrice(p.price)}€` : `$${fmtPrice(p.price)}`)}</span>
              {p.price > 0 && <span className="text-stone-500">{t("pricing_month")}</span>}
            </div>
            <ul className="space-y-3 flex-1 mb-6">
              {featsOf(p).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0"><Check size={13} /></span>
                  <span className="text-stone-700">{f}</span>
                </li>
              ))}
            </ul>
            <Button data-testid={`plan-cta-${p.id}`}
              onClick={() => (p.id === "free" ? nav(user ? "/earn" : "/auth?mode=signup") : nav(`/checkout?plan=${p.id}`))}
              className={`rounded-full min-h-[52px] font-bold text-base ${p.highlight ? "bt-gradient text-white hover:opacity-90" : "bg-primary hover:bg-[#6D28D9] text-white"}`}>
              {p.id === "free" ? t("pricing_free_cta") : t("pricing_choose") + " " + nameOf(p)}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-center text-stone-400 text-sm max-w-2xl mx-auto" data-testid="pricing-disclaimer">{t("earnings_note")}</p>
    </div>
  );
}
